import React, { useEffect, useState } from "react";

import _ from 'lodash';
import cx from 'classnames';

import Wizard from "src/components/wizard";
import { CallSettings } from "./components/callSettings";
import { ScenarioSettings } from "./components/scenarios";
import { SimulationContext, SimulationContextProps } from "./context";
import { animated, useTransition } from "@react-spring/web";
import { PersonaEditor } from "./components/scenarios/persona";

import styles from "./edit.module.scss";
import { SimulationMenuBar } from "../components/menu";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AssessmentParameter, Simulation, SimulationValidationError } from "src/types/coaching/simulation";
import { editSimulation, fetchDraftSimulation, publishSimulation, updateSimulation, updateValidationErrorsSimulation } from "src/redux/actions/coaching/simulation";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";
import { prepareSimulationRequest, validateSimulationRequest } from "./helper";
import { OrganizationEditor } from "./components/scenarios/organization";
import { ObjectivesEditor } from "./components/scenarios/objectives";
import { AssessmentSettings } from "./components/assessments";
import { AssessmentParameterEditor } from "./components/assessments/editor";

import userRoleService from "src/services/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { SimulationNotFound } from "src/pages/coaching/components/notFound";

export const EditSimulationPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const params = useParams();
  const id = params.id;

  const persistedSimulation: Simulation = useSelector(
    (state: any) => _.get(state, `coaching.simulations.simulation[${id}].item`) as Simulation,
  );

  const loading = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.simulation[${id}].loading`);
  });

  const simulation: Simulation = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.edit.item`) as Simulation;
  });

  const simulationErrorCode = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.simulation[${id}].error.errors[0].code`);
  });

  const [currentStep, setCurrentStep] = React.useState(0);

  const [editPersona, setEditPersona] = React.useState(false);
  const [editObjectives, setEditObjectives] = React.useState(false);
  const [editOrganizationalInfo, setEditOrganizationalInfo] = React.useState(false);
  const [editAssessmentParamater, setEditAssessmentParamater] = React.useState<AssessmentParameter | null>(null);

  const [contextState, setContextState] = useState<SimulationContextProps>({});

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null,
  });

  const personaTransition = useTransition(editPersona, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const organizationTransition = useTransition(editOrganizationalInfo, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const objectivesTransition = useTransition(editObjectives, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const assessmentTransition = useTransition(editAssessmentParamater, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const handleEditPersona = (value: boolean) => {
    setEditPersona(value);
  }

  const handleEditOrganizationalInfo = (value: boolean) => {
    setEditOrganizationalInfo(value);
  }

  const handleEditObjectives = (value: boolean) => {
    setEditObjectives(value);
  }

  const handleEditAssessmentParameter = (value: AssessmentParameter | null) => {
    setEditAssessmentParamater(value);
  }

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  const handleMenuStateChange = (state) => {
    setMenuState(state);
  };

  const handleGoToError = (error?: SimulationValidationError) => {
    setContextState((prev) => {
      return {
        ...prev,
        validationError: error,
      };
    });
  };

  const handleSave = () => {
    const validationErrors = validateSimulationRequest(simulation);

    if (_.size(validationErrors) > 0) {
      dispatch(updateValidationErrorsSimulation({
        simulation: simulation.id!,
        errors: validationErrors,
      }));
    }

    const request = prepareSimulationRequest(persistedSimulation, simulation);

    dispatch(
      updateSimulation({
        id: simulation.id!,
        request: request,
      }),
    );
  };

  const handlePublish = () => {
    const validationErrors = validateSimulationRequest(simulation);

    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsSimulation({
          simulation: simulation.id!,
          errors: validationErrors,
        }),
      );

      return;
    }

    dispatch(publishSimulation(simulation.id!));
  };

  useEffect(() => {
    setContextState({
      gotoError: handleGoToError,
      editPersona: handleEditPersona,
      editObjectives: handleEditObjectives,
      editOrganizationInfo: handleEditOrganizationalInfo,
      editAssessmentParameter: handleEditAssessmentParameter,
    });
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(fetchDraftSimulation(id));

      const courseLearnerRequest = {
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      };

      dispatch(fetchCourseLearners(courseLearnerRequest));
    }

    setContextState((prev) => {
      return {
        ...prev,
        id: id,
        gotoError: handleGoToError,
        editPersona: handleEditPersona,
        editObjectives: handleEditObjectives,
        editOrganizationInfo: handleEditOrganizationalInfo,
      };
    });
  }, [id]);

  useEffect(() => {
    if (persistedSimulation && persistedSimulation.id !== simulation?.id) {
      dispatch(editSimulation(persistedSimulation));
    }
  }, [persistedSimulation, simulation]);

  useEffect(() => {
    if (menuState.primaryAction === "save") {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    } else if (menuState.primaryAction === "publish") {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    }
  }, [menuState]);

  useEffect(() => {
    if (contextState.validationError) {
      const type = contextState.validationError.type;

      switch (type) {
        case "call-settings": {
          setCurrentStep(0);
          break;
        }

        case "organization": {
          setCurrentStep(1);
          setEditOrganizationalInfo(true);
          break;
        }

        case "persona": {
          setCurrentStep(1);
          setEditPersona(true);
          break;
        }

        case "objectives": {
          setCurrentStep(1);
          setEditObjectives(true);
          break;
        }

        default: {
          break;
        }
      }
    }
  }, [contextState.validationError]);

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (simulationErrorCode === 10007) {
    return <SimulationNotFound onBack={() => {
      navigate(`/coaching/manage`);
    }} />
  }

  if ((loading && simulation?.id != id) || !simulation) {
    return <div className={styles.loading}>Loading simulation...</div>
  }

  return (
    <SimulationContext.Provider value={contextState}>
      <div className={cx(styles.createSimulationContainer, {
        [styles.noScroll]: editPersona || editObjectives || editOrganizationalInfo || editAssessmentParamater,
      })}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div
              className={styles.back}
              onClick={() => {
                navigate(`/coaching/manage`);
              }}
            >
              <i className="paperflite-left-arrow"></i>
              <span>courses</span>
            </div>
          </div>
          <div className={styles.menubar}>
            <SimulationMenuBar
              simulation={simulation}
              menuState={menuState}
              onMenuStateChange={handleMenuStateChange}
            />
          </div>
        </div>

        <Wizard.Pane
          className={cx(styles.wizard, {})}
          currentStep={currentStep}
          gap={40}
          onStepChange={handleStepChange}
          wideView={false}
        >
          <Wizard.Step
            className={styles.wizardStep}
            name="Step up"
            completed={true}
          >
            <CallSettings simulation={simulation} />
          </Wizard.Step>
          <Wizard.Step name="Scenario" completed={true}>
            <ScenarioSettings simulation={simulation} />
          </Wizard.Step>

          <Wizard.Step name="Assessment" completed={true}>
            <AssessmentSettings simulation={simulation} />
          </Wizard.Step>
        </Wizard.Pane>
      </div>

      {organizationTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <OrganizationEditor simulation={simulation} />
          </animated.div>
        ) : false;
      })}

      {personaTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <PersonaEditor simulation={simulation} />
          </animated.div>
        ) : false;
      })}

      {objectivesTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <ObjectivesEditor simulation={simulation} />
          </animated.div>
        ) : false;
      })}

      {assessmentTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <AssessmentParameterEditor simulation={simulation} assessmentParameter={item} />
          </animated.div>
        ) : false;
      })}
    </SimulationContext.Provider>
  );
};
